import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api, problemDetail } from '@/services/api'

interface Feedback { is_correct: boolean; explanation: string | null }
interface Question {
  question_id: string; type: string; statement: string
  options: { id: string; text: string }[]
  attempts: number; last_answer: string | null; feedback: Feedback | null
}
interface Task {
  id: string; title: string; description: string | null; due_at: string
  status: string; feedback_mode: string; max_attempts: number
}
interface Detail extends Task {
  items: Question[]; accepting: boolean; submitted_at: string | null; feedback_available: boolean
}
interface TaskPage { items: Task[]; page_info: { has_more: boolean; next_cursor: string | null } }

export function TasksView({ onClose }: { onClose: () => void }) {
  const [selected, setSelected] = useState<string | null>(null)
  const [cursor, setCursor] = useState<string | null>(null)
  const tasks = useQuery({
    queryKey: ['tasks', cursor],
    queryFn: async () => (await api.get<TaskPage>('/tasks', { params: { cursor } })).data,
    retry: false,
  })
  if (selected) return <TaskDetail key={selected} taskId={selected} onBack={() => setSelected(null)} />
  return <main className="assessment-view">
    <button type="button" onClick={onClose}>Voltar à biblioteca</button>
    <h1>Minhas avaliações</h1>
    {tasks.isPending && <p role="status">Carregando avaliações…</p>}
    {tasks.isError && <p role="alert">Não foi possível carregar as avaliações. <button type="button" onClick={() => { void tasks.refetch() }}>Tentar novamente</button></p>}
    {tasks.data?.items.length === 0 && <p>Você ainda não tem avaliações publicadas.</p>}
    <ul className="assessment-list">{tasks.data?.items.map(task => <li key={task.id}>
      <h2>{task.title}</h2><p>Prazo: {new Date(task.due_at).toLocaleString('pt-BR')}</p>
      <p>{task.status === 'closed' || new Date(task.due_at) <= new Date() ? 'Prazo encerrado — consulte suas respostas' : 'Disponível para responder'}</p>
      <button type="button" onClick={() => setSelected(task.id)}>Abrir avaliação: {task.title}</button>
    </li>)}</ul>
    {cursor && <button type="button" onClick={() => setCursor(null)}>Primeira página</button>}
    {tasks.data?.page_info.has_more && <button type="button" onClick={() => setCursor(tasks.data?.page_info.next_cursor ?? null)}>Próxima página</button>}
  </main>
}

function TaskDetail({ taskId, onBack }: { taskId: string; onBack: () => void }) {
  const client = useQueryClient()
  const query = useQuery({ queryKey: ['task', taskId], queryFn: async () => (await api.get<Detail>(`/tasks/${taskId}`)).data, retry: false })
  const submit = useMutation({
    mutationFn: async () => api.post(`/tasks/${taskId}/submit`),
    onSuccess: async () => { await client.invalidateQueries({ queryKey: ['task', taskId] }) },
  })
  const task = query.data
  const answered = task?.items.filter(q => q.attempts > 0).length ?? 0
  return <main className="assessment-view">
    <button type="button" onClick={onBack}>Voltar às avaliações</button>
    {query.isPending && <p role="status">Abrindo avaliação…</p>}
    {query.isError && <p role="alert">{problemDetail(query.error) || 'Não foi possível abrir a avaliação.'} <button type="button" onClick={() => { void query.refetch() }}>Tentar novamente</button></p>}
    {task && <>
      <h1>{task.title}</h1><p>{task.description}</p>
      <p>Prazo: {new Date(task.due_at).toLocaleString('pt-BR')} · Até {task.max_attempts} tentativa(s) por questão.</p>
      <p>{task.feedback_mode === 'deferred' ? 'A correção será exibida após o prazo de entrega.' : 'A correção aparece após cada resposta.'}</p>
      <p role="status">{answered} de {task.items.length} questões respondidas</p>
      {task.submitted_at ? <section role="status"><h2>Avaliação entregue</h2><p>Entrega registrada em {new Date(task.submitted_at).toLocaleString('pt-BR')}.</p></section> : !task.accepting && <p role="status">Esta avaliação não aceita mais respostas.</p>}
      {task.items.map((question, i) => <QuestionForm key={question.question_id} task={task} question={question} number={i+1} />)}
      {task.accepting && <button type="button" disabled={answered !== task.items.length || submit.isPending} onClick={() => submit.mutate()}>{submit.isPending ? 'Entregando…' : 'Entregar avaliação'}</button>}
      {submit.isError && <p role="alert">{problemDetail(submit.error) || 'Não foi possível entregar. Tente novamente.'}</p>}
      <button type="button" onClick={() => { void query.refetch() }}>Atualizar resultados</button>
    </>}
  </main>
}

function QuestionForm({ task, question, number }: { task: Detail; question: Question; number: number }) {
  const [answer, setAnswer] = useState(question.last_answer ?? '')
  const attemptId = useRef<{ value: string; id: string } | null>(null)
  const [openedAt] = useState(Date.now)
  const started = useRef(openedAt)
  const client = useQueryClient()
  const mutation = useMutation({
    mutationFn: async () => {
      if (attemptId.current?.value !== answer) attemptId.current = { value: answer, id: crypto.randomUUID() }
      return api.post(`/tasks/${task.id}/attempts`, { question_id: question.question_id,
        value: answer, client_id: attemptId.current.id, time_spent_sec: Math.min(86400, Math.max(0, Math.round((Date.now() - started.current) / 1000))) })
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['task', task.id] })
      attemptId.current = null
      started.current = Date.now()
    },
  })
  const disabled = !task.accepting || question.attempts >= task.max_attempts || mutation.isPending
  const options = question.type === 'true_false' ? [{ id: 'true', text: 'Verdadeiro' }, { id: 'false', text: 'Falso' }] : question.options
  return <form className="assessment-question" onSubmit={event => { event.preventDefault(); mutation.mutate() }}>
    <fieldset disabled={disabled}><legend>{number}. {question.statement}</legend>
      {question.type === 'fill_blank' ? <label>Sua resposta<input value={answer} onChange={e => setAnswer(e.target.value)} maxLength={2000} required /></label> : options.map(option => <label key={option.id} className="exercise-option">
        <input type="radio" name={`question-${question.question_id}`} value={option.id} checked={answer === option.id} onChange={() => setAnswer(option.id)} required />{option.text}
      </label>)}
    </fieldset>
    <p>{question.attempts} de {task.max_attempts} tentativa(s) utilizada(s)</p>
    {!disabled && <button type="submit" disabled={!answer.trim()}>Confirmar resposta da questão {number}</button>}
    {mutation.isPending && <p role="status">Registrando resposta…</p>}
    {mutation.isError && <p role="alert">{problemDetail(mutation.error) || 'Falha ao registrar resposta. Tente novamente.'}</p>}
    {question.attempts > 0 && <div role="status">{question.feedback ? <><p>{question.feedback.is_correct ? 'Resposta correta!' : 'Resposta incorreta.'}</p><p>{question.feedback.explanation}</p></> : <p>Resposta registrada. Correção disponível após o prazo.</p>}</div>}
  </form>
}
